(function () {
  'use strict';

  class BotanicalInhalerSelector {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;

      const configEl = document.getElementById('inhaler-config-' + this.sectionId);
      if (!configEl) {
        console.warn('[InhalerSelector] Config element not found for section:', this.sectionId);
        return;
      }

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (err) {
        console.error('[InhalerSelector] Failed to parse config JSON:', err);
        console.error('[InhalerSelector] Raw JSON:', configEl.textContent);
        return;
      }

      console.log('[InhalerSelector] Initialized with', this.config.scents.length, 'scents,', this.config.kits.length, 'kits');

      this.state = {
        mode: 'single',
        selectedScent: this.config.scents[0]?.key || '',
        selectedCategory: this.config.scents[0]?.category || '',
        selectedKit: null,
        byoScents: [],
        subscribeEnabled: false,
        kitSubscribeEnabled: false,
      };

      // Set initial kit to first curated kit if available
      if (this.config.kits.length > 0) {
        this.state.selectedKit = 0; // index into config.kits
      }

      this.initGallery();
      this.bindEvents();
      this.updatePriceDisplay();
    }

    // ── Gallery (Swiper) ──

    initGallery() {
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.variantMediaMap = this.config.variantMediaMap || {};

      const mainSwiperEl = this.section.querySelector('[data-bi-main-swiper]');
      const thumbsSwiperEl = this.section.querySelector('[data-bi-thumbs-swiper]');
      if (!mainSwiperEl) return;

      const initSwipers = () => {
        // Init thumbs first
        if (thumbsSwiperEl) {
          this.thumbsSwiper = new Swiper(thumbsSwiperEl, {
            spaceBetween: 8,
            slidesPerView: 'auto',
            freeMode: true,
            watchSlidesProgress: true,
          });
        }

        const mainConfig = {
          spaceBetween: 0,
          grabCursor: true,
          navigation: {
            nextEl: mainSwiperEl.querySelector('.swiper-button-next'),
            prevEl: mainSwiperEl.querySelector('.swiper-button-prev'),
          },
        };

        if (this.thumbsSwiper) {
          mainConfig.thumbs = { swiper: this.thumbsSwiper };
        }

        this.mainSwiper = new Swiper(mainSwiperEl, mainConfig);

        // Slide to variant from URL param
        const urlVariant = new URLSearchParams(window.location.search).get('variant');
        if (urlVariant) {
          this.slideToVariant(parseInt(urlVariant, 10));
          // Also select the matching scent
          const matchingScent = this.config.scents.find(s => String(s.variantId) === urlVariant);
          if (matchingScent) {
            this.selectScent(matchingScent.key);
          }
        }
      };

      if (typeof Swiper !== 'undefined') {
        initSwipers();
      } else {
        const check = setInterval(() => {
          if (typeof Swiper !== 'undefined') {
            clearInterval(check);
            initSwipers();
          }
        }, 50);
      }
    }

    slideTo(index) {
      if (!this.mainSwiper || index < 0 || index >= this.mainSwiper.slides.length) return;
      this.mainSwiper.slideTo(index);
    }

    slideToVariant(variantId) {
      if (!variantId) return;
      const slideIndex = this.variantMediaMap[String(variantId)];
      if (slideIndex !== undefined) this.slideTo(slideIndex);
    }

    // ── Helpers ──

    getScent(key) {
      return this.config.scents.find(s => s.key === key);
    }

    formatMoney(cents) {
      // Simple money formatter - uses Shopify money_format
      const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return '$' + amount;
    }

    // ── Event Binding (delegation) ──

    bindEvents() {
      this.section.addEventListener('click', (e) => {
        const target = e.target.closest('[data-mode]');
        if (target) return this.switchMode(target.dataset.mode);

        const catTab = e.target.closest('[data-category]');
        if (catTab) return this.selectCategory(catTab.dataset.category);

        const scentPill = e.target.closest('.bi-scent-pill');
        if (scentPill && !scentPill.disabled) return this.selectScent(scentPill.dataset.scent);

        const byoPill = e.target.closest('.bi-byo-pill');
        if (byoPill && !byoPill.disabled) return this.toggleByoScent(byoPill.dataset.scent);

        const kitCard = e.target.closest('.bi-kit-card');
        if (kitCard) return this.selectKit(kitCard);

        const removeTag = e.target.closest('.bi-remove-tag');
        if (removeTag) return this.removeByoScent(removeTag.dataset.scent);

        const singleAtc = e.target.closest('[data-single-atc]');
        if (singleAtc) return this.addSingleToCart();

        const kitAtc = e.target.closest('[data-kit-atc]');
        if (kitAtc && !kitAtc.disabled) return this.addKitToCart();

        const collapsibleHeader = e.target.closest('.bi-collapsible-header');
        if (collapsibleHeader) return this.toggleCollapsible(collapsibleHeader);
      });

      this.section.addEventListener('change', (e) => {
        const toggle = e.target.closest('[data-subscribe-toggle]');
        if (toggle) return this.toggleSubscribe(toggle.dataset.subscribeToggle, toggle.checked);
      });
    }

    // ── Collapsible Accordion ──

    toggleCollapsible(header) {
      const collapsible = header.closest('.bi-collapsible');
      const content = collapsible.querySelector('.bi-collapsible-content');
      if (!content) return;

      const isOpen = collapsible.classList.contains('open');

      if (isOpen) {
        // Close: animate from current height to 0
        content.style.height = content.scrollHeight + 'px';
        // Force reflow so the browser registers the starting height
        content.offsetHeight;
        content.style.height = '0';
        collapsible.classList.remove('open');
        header.setAttribute('aria-expanded', 'false');
      } else {
        // Open: animate from 0 to scrollHeight
        content.style.height = content.scrollHeight + 'px';
        collapsible.classList.add('open');
        header.setAttribute('aria-expanded', 'true');

        // After transition, set height to auto so content reflows naturally
        const onEnd = () => {
          content.style.height = 'auto';
          content.removeEventListener('transitionend', onEnd);
        };
        content.addEventListener('transitionend', onEnd);
      }
    }

    // ── Mode Switching ──

    switchMode(mode) {
      this.state.mode = mode;

      this.section.querySelectorAll('.bi-mode-tab').forEach(tab => {
        const isActive = tab.dataset.mode === mode;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive);
      });

      this.section.querySelectorAll('.bi-panel').forEach(panel => {
        panel.classList.toggle('active', panel.id === 'bi-panel-' + mode);
      });
    }

    // ── Single Panel: Category Selection ──

    selectCategory(key) {
      this.state.selectedCategory = key;

      this.section.querySelectorAll('.bi-category-tab').forEach(tab => {
        const isActive = tab.dataset.category === key;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive);
      });

      this.section.querySelectorAll('.bi-category-panel').forEach(panel => {
        panel.classList.toggle('active', panel.dataset.categoryPanel === key);
      });
    }

    // ── Single Panel: Scent Selection ──

    selectScent(key) {
      this.state.selectedScent = key;
      const scent = this.getScent(key);
      if (!scent) return;

      // Update active pill
      this.section.querySelectorAll('#bi-panel-single .bi-scent-pill').forEach(pill => {
        pill.classList.toggle('active', pill.dataset.scent === key);
      });

      // Update name display
      const nameEl = this.section.querySelector('[data-scent-name]');
      if (nameEl) nameEl.textContent = scent.name;

      // Update description
      const descEl = this.section.querySelector('[data-scent-desc]');
      if (descEl) {
        descEl.innerHTML =
          '<div class="bi-desc-title">' + scent.desc + '</div>' +
          '<div class="bi-desc-ingredients">' + scent.ingredients + '</div>';
      }

      // Slide gallery to variant image
      this.slideToVariant(scent.variantId);

      this.updatePriceDisplay();
    }

    // ── Subscribe Toggle ──

    toggleSubscribe(context, checked) {
      if (context === 'single') {
        this.state.subscribeEnabled = checked;
      } else {
        this.state.kitSubscribeEnabled = checked;
      }
      this.updatePriceDisplay();
    }

    // ── Kit Selection ──

    selectKit(cardEl) {
      const kitKey = cardEl.dataset.kit;

      // Update active card
      this.section.querySelectorAll('.bi-kit-card').forEach(card => {
        card.classList.toggle('active', card === cardEl);
      });

      if (kitKey === 'byo') {
        this.state.selectedKit = 'byo';
        this.section.querySelector('[data-byo-expand]')?.classList.add('show');
      } else {
        // Find kit index by matching the block id stored in data-kit
        const kitIndex = this.config.kits.findIndex(k => {
          // Match by scents string since kit card stores scent keys
          return cardEl.dataset.kitScents === k.scents;
        });
        this.state.selectedKit = kitIndex >= 0 ? kitIndex : 0;
        this.section.querySelector('[data-byo-expand]')?.classList.remove('show');
      }

      this.updateKitCartButton();
    }

    // ── BYO: Toggle Scent ──

    toggleByoScent(key) {
      const idx = this.state.byoScents.indexOf(key);
      if (idx > -1) {
        this.state.byoScents.splice(idx, 1);
      } else {
        if (this.state.byoScents.length >= this.config.kitSize) return;
        this.state.byoScents.push(key);
      }
      this.syncByo();
    }

    removeByoScent(key) {
      this.state.byoScents = this.state.byoScents.filter(s => s !== key);
      this.syncByo();
    }

    syncByo() {
      const count = this.state.byoScents.length;
      const max = this.config.kitSize;

      // Update counter
      const counter = this.section.querySelector('[data-byo-counter]');
      if (counter) {
        counter.textContent = count === max ? 'All ' + max + ' selected!' : count + ' of ' + max + ' selected';
        counter.classList.toggle('complete', count === max);
      }

      // Update pills
      this.section.querySelectorAll('.bi-byo-pill').forEach(pill => {
        const key = pill.dataset.scent;
        const isSelected = this.state.byoScents.includes(key);
        pill.classList.toggle('active', isSelected);
        pill.classList.toggle('disabled', !isSelected && count >= max);
        pill.disabled = !isSelected && count >= max;
      });

      // Update tags
      const tagsEl = this.section.querySelector('[data-byo-tags]');
      if (tagsEl) {
        tagsEl.innerHTML = this.state.byoScents.map(key => {
          const scent = this.getScent(key);
          return '<span class="bi-byo-tag">' +
            (scent ? scent.name : key) +
            '<button class="bi-remove-tag" data-scent="' + key + '" aria-label="Remove ' + (scent ? scent.name : key) + '">&times;</button>' +
            '</span>';
        }).join('');
      }

      this.updateKitCartButton();
    }

    // ── Price Display ──

    updatePriceDisplay() {
      const singlePrice = this.config.singlePrice;
      const subDiscount = this.config.subDiscount;

      // Single price
      const displayPrice = this.state.subscribeEnabled
        ? Math.round(singlePrice * (100 - subDiscount) / 100)
        : singlePrice;

      const priceEl = this.section.querySelector('[data-single-price]');
      if (priceEl) priceEl.textContent = this.formatMoney(displayPrice);

      // Single ATC button
      const singleAtc = this.section.querySelector('[data-single-atc]');
      if (singleAtc) {
        const suffix = this.state.subscribeEnabled ? '/mo' : '';
        singleAtc.textContent = 'ADD TO CART \u2014 ' + this.formatMoney(displayPrice) + suffix;
      }

      this.updateKitCartButton();
    }

    updateKitCartButton() {
      const kitAtc = this.section.querySelector('[data-kit-atc]');
      if (!kitAtc) return;

      const kitSize = this.config.kitSize;
      const singlePrice = this.config.singlePrice;
      const bundleDiscount = this.config.bundleDiscount;
      const subDiscount = this.config.subDiscount;

      let kitTotal = Math.round(singlePrice * kitSize * (100 - bundleDiscount) / 100);
      if (this.state.kitSubscribeEnabled) {
        kitTotal = Math.round(kitTotal * (100 - subDiscount) / 100);
      }

      if (this.state.selectedKit === 'byo' && this.state.byoScents.length < kitSize) {
        kitAtc.disabled = true;
        const remaining = kitSize - this.state.byoScents.length;
        kitAtc.textContent = 'SELECT ' + remaining + ' MORE SCENT' + (remaining > 1 ? 'S' : '');
      } else {
        kitAtc.disabled = false;
        const suffix = this.state.kitSubscribeEnabled ? '/mo' : '';
        kitAtc.textContent = 'ADD KIT TO CART \u2014 ' + this.formatMoney(kitTotal) + suffix;
      }
    }

    // ── Cart API ──

    addSingleToCart() {
      const scent = this.getScent(this.state.selectedScent);
      if (!scent || !scent.variantId) return;

      const body = {
        items: [{
          id: scent.variantId,
          quantity: 1,
        }],
      };

      if (this.state.subscribeEnabled) {
        const toggleEl = this.section.querySelector('[data-subscribe-toggle="single"]');
        const planId = toggleEl?.dataset.sellingPlanId;
        if (planId) body.items[0].selling_plan = parseInt(planId, 10);
      }

      this.postToCart(body);
    }

    addKitToCart() {
      let scentKeys;

      if (this.state.selectedKit === 'byo') {
        scentKeys = this.state.byoScents;
      } else {
        const kit = this.config.kits[this.state.selectedKit];
        if (!kit) return;
        scentKeys = kit.scents.split(',').map(k => k.trim());
      }

      // Resolve scent keys to variant IDs
      const items = scentKeys
        .map(key => {
          const scent = this.getScent(key);
          if (!scent || !scent.variantId) return null;
          const item = { id: scent.variantId, quantity: 1 };
          // Attach selling plan if subscribe is enabled
          if (this.state.kitSubscribeEnabled) {
            const toggleEl = this.section.querySelector('[data-subscribe-toggle="kits"]');
            const planId = toggleEl?.dataset.sellingPlanId;
            if (planId) item.selling_plan = parseInt(planId, 10);
          }
          return item;
        })
        .filter(Boolean);

      if (items.length === 0) return;

      this.postToCart({ items });
    }

    async postToCart(body) {
      const atcButtons = this.section.querySelectorAll('.bi-add-to-cart, .bi-kit-add-to-cart');
      atcButtons.forEach(btn => { btn.disabled = true; });

      // Request section rendering for cart drawer update
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      if (document.querySelector('cart-notification')) {
        sections.push('cart-notification-product', 'cart-notification-button');
      }
      body.sections = sections;
      body.sections_url = window.location.pathname;

      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.message || result.description || 'Cart add failed');
        }

        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // Update and open cart drawer (matches theme pattern from block-purchase-options)
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');

        if (cartDrawer) {
          try {
            const sectionResponse = await fetch(window.location.pathname + '?sections=cart-drawer');
            const sectionsData = await sectionResponse.json();

            if (sectionsData['cart-drawer']) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionsData['cart-drawer'], 'text/html');
              const newDrawer = doc.querySelector('cart-drawer');

              if (newDrawer) {
                cartDrawer.replaceWith(newDrawer);
                const updatedDrawer = document.querySelector('cart-drawer');

                setTimeout(() => {
                  if (updatedDrawer) {
                    if (typeof updatedDrawer.open === 'function') {
                      updatedDrawer.open();
                    } else {
                      updatedDrawer.classList.add('active', 'animate');
                      updatedDrawer.style.visibility = 'visible';
                    }
                  }
                }, 100);
              }
            }
          } catch (err) {
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          }
        } else if (cartNotification) {
          cartNotification.renderContents?.(result);
        } else {
          window.location.href = '/cart';
        }

        // Update cart count bubble
        try {
          const cartResponse = await fetch('/cart.json');
          const cart = await cartResponse.json();
          const countBubble = document.querySelector('.cart-count-bubble span');
          if (countBubble) countBubble.textContent = cart.item_count;
        } catch (e) {
          // Ignore
        }
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        atcButtons.forEach(btn => { btn.disabled = false; });
        this.updatePriceDisplay();
      }
    }
  }

  // ── Initialize ──

  function init() {
    document.querySelectorAll('.bi-section').forEach(section => {
      new BotanicalInhalerSelector(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
